import { HeaderComponent } from "@/components/headerComponet";
import { ActiveItemProvider } from "../home/context/ActiveItemContext";
import DrawerComponent from "@/components/DrawerComponent";
import { SectionContainer } from "@/components/SectionContainer/SectionContainer";
import { DataGrid, GridColDef, GridRowsProp, MuiEvent, GridCellEditStopParams, GridValidRowModel } from "@mui/x-data-grid";
import AddIcon from '@mui/icons-material/Add';

import "./style.css";
import { useEffect, useState, useRef } from "react";
import clientApi from "@/services/api/client";
import { FormGroupRegister } from "../home/Components/formGrouRegister";
import { ClientContextProvider, useClient } from "@/contexts/ClientContext";
import { ModalComponent } from "../home/Components/Modal";
import { SelecttButton } from "./SelectButton";
import UserService from "@/services/UserService"; // Certifique-se de importar o UserService e APIs necessárias
import attendantApi from "@/services/api/attendant";
import attendanceApi from "@/services/api/attendance";
import briefingApi from "@/services/api/briefing";
import { useNavigate } from "react-router-dom";
import { CustomSnackbar } from "@/utils/alert";
import { delay } from "@/utils/delay";
import { safeJsonParse } from "@/utils/safeJsonParse";
import { UserRole } from "@/enums/UserRole";

interface SelectButtonRef {
  clearSelection: () => void;
}

type Severity = "success" | "error";

export function ClientPage() {
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [creatingClient, setCreatingClient] = useState(false);
  const [deleteId, setDeleteId] = useState<string | number | undefined>(undefined);
  const [showAlert, setShowAlert] = useState(false);
  const selectButtonRef = useRef<SelectButtonRef>(null);
  const navigate = useNavigate();
  const [alertInfo, setAlertInfo] = useState({
    severity: "success" as Severity,
    message: "",
  });

  const clientContext = useClient();

  useEffect(() => {
    clientContext.reloadClientes();
  }, []);

  useEffect(() => {
    let clientsRow: GridValidRowModel[] = [];
    const user = UserService.getUser();
    const attendantRaw = localStorage.getItem("attendant");
    const attendant = attendantRaw ? safeJsonParse(attendantRaw) : null;

    const isProjetista = user?.role?.includes(UserRole.PROJETISTA);
    const atendenteId = isProjetista ? attendant?.id : undefined;

    clientApi.list(atendenteId).then((response) => {
      const clients = response.data.clients ?? undefined;

      if (response.status === 500) {
        console.error("houve um erro ao buscar os clientes: ", response);
        return;
      }

      if (!clients || clients.length === 0) {
        setRows([]);
        return;
      }

      clientsRow = clients.map((client: any) => {
        return {
          id: client.id,
          nome: client.nome,
          telefone: client.telefone,
          email: client.email,
          cpf: client.cpf,
          estado: client.atendimentos.map((atendimento: any) => { return atendimento.estado })
        };
      });
      setRows(clientsRow);
    });
  }, [clientContext.clientes, clientContext]);

  const handleCreate = (id: number) => {
    createAttendance(id);
  };

  const createAttendance = async (clientId: number) => {
    try {
      const userId = UserService.getUser()?.userId;
      if (!userId) throw new Error("Não há informação de userId");

      const attendantResponse = await attendantApi.getAttendantByUserId(userId);
      if (attendantResponse.status === 500) throw new Error("Erro ao recuperar atendente");

      const attendant = attendantResponse.data;
      if (!attendant) throw new Error("Erro ao recuperar dados do atendente, atendente provavelmente não existe com este id");

      const attendanceResponse = await attendanceApi.createAttendance(clientId, attendant.id);
      if (attendanceResponse.status === 500) throw new Error("Erro ao criar atendimento");

      const attendanceId = attendanceResponse.data.id;

      const briefingResponse = await briefingApi.createBriefing(attendanceId);
      if (briefingResponse.status === 500 || !briefingResponse.data) throw new Error("Erro ao criar briefing");

      setAlertInfo({ severity: "success", message: "Atendimento criado com sucesso! redirecionando..." });
      setShowAlert(true);

      await delay(1500);

      setShowAlert(false);
      navigate('/homepage');

    } catch (error) {
      console.error('Erro ao criar atendimento e briefing:', error);
      setAlertInfo({ severity: "error", message: "Erro ao criar atendimento e briefing." });
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    if (deleteId !== undefined) {
      const request = await clientApi.deleteClient(deleteId.toString());
      if (request.status === 200) {
        clientContext.reloadClientes();
        setDeleteId(undefined);
        setAlertInfo({ severity: "success", message: "Cliente removido com sucesso!" });
        setShowAlert(true);
      } else {
        console.error("houve um erro ao remover o cliente: " + deleteId, request);
        setAlertInfo({ severity: "error", message: "Erro ao remover cliente." });
        setShowAlert(true);
      }
    }
  };

  const handleAlertClose = () => {
    setShowAlert(false);
  };

  const columns: GridColDef[] = [
    { field: 'nome', headerName: 'Nome', width: 300, editable: true },
    { field: 'telefone', headerName: 'Telefone', width: 200, editable: true },
    { field: 'email', headerName: 'Email', width: 300, editable: true },
    { field: 'cpf', headerName: 'CPF', width: 150, editable: true },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 150,
      renderCell: (params: { value?: any[] }) => (
        <div className="estados-container">
          {params.value?.map((estado: any, index: number) => (
            <div key={index} className="estado">
              {estado}
            </div>
          ))}
        </div>
      )
    },
    {
      field: "actions",
      headerName: "Ações",
      sortable: false,
      maxWidth: 70,
      flex: 1,
      renderCell: (params) => (
        <SelecttButton
          ref={selectButtonRef}
          id={params.row.id}
          name={params.row.name}
          onCreate={() => handleCreate(params.row.id)}
          onDelete={() => setDeleteId(params.row.id)}
        />
      ),
    },
  ];

  function handleClientUpdate(params: GridCellEditStopParams, event: MuiEvent & { target?: { value: string } }) {

    const id = params.id;
    const field = params.field;
    const newValue = event.target?.value;

    const client = rows.find((row) => row.id === id);
    const telefone = client?.telefone;
    const nome = client?.nome;

    const data = {
      nome,
      telefone,
      [field]: newValue,
    };

    clientApi.updateClient(Number(id), { dataClient: data });
  }

  return (
    <div className="client-page" id="client-page">
      <ActiveItemProvider>
        <DrawerComponent />
      </ActiveItemProvider>

      <HeaderComponent />

      <div className="content-container">
        <h1 className="title-table">Clientes</h1>

        <div className="create-client-button" onClick={() => { setCreatingClient(true) }}>
          <AddIcon /> Cliente
        </div>

        <div className="second-section">
          <SectionContainer>
            <DataGrid
              columns={columns}
              rows={rows}
              onCellEditStop={handleClientUpdate}
              className="data-grid"
            />
          </SectionContainer>
          {
            creatingClient && (
              <ClientContextProvider>
                <ModalComponent onClose={() => { setCreatingClient(false); clientContext.resetClient() }} open={creatingClient}>
                  <div className="modal-container">
                    <div className="form-section">
                      <div className="create-client-title title-table">
                        Novo cliente
                      </div>
                      <FormGroupRegister isUpdateMode={false} />
                    </div>
                  </div>
                </ModalComponent>
              </ClientContextProvider>
            )
          }
        </div>
      </div>

      {
        deleteId && (
          <ModalComponent open={deleteId !== undefined} onClose={() => { setDeleteId(undefined) }}>
            <div className="modal-container confirmation-modal">
              <div className="confirmation-container">
                <div className="confirmation-title">
                  Deseja realmente apagar o cliente?
                </div>
                <div className="confirmation-buttons">
                  <button className="cancel-button" onClick={() => { setDeleteId(undefined) }}>
                    Cancelar
                  </button>
                  <button className="confirmation-button" onClick={() => { handleDelete(); setDeleteId(undefined) }}>
                    Confirmar
                  </button>
                </div>
              </div>
            </div>
          </ModalComponent>
        )
      }

      <CustomSnackbar
        open={showAlert}
        severity={alertInfo.severity}
        message={alertInfo.message}
        handleClose={handleAlertClose}
      />
    </div>
  );
}
